/**
 * Modal de confirmación con token para borrar los archivos del cajón de un
 * documento: contabilización, vinculado y XML.
 *
 * Pedido por las supervisoras (reunión 2026-09-18). Mismo token mensual que
 * ya protege el borrado de carpetas, misma forma y mismo texto que el modal
 * de borrado de carpetas para que no haya que aprender dos mecanismos. La regla y sus
 * límites están documentados en lib/deletion_token.js.
 *
 * Lo consume documents/file.js.
 */

import React, { useState, useEffect } from 'react';
import { useFirebase } from 'react-redux-firebase';
import { Modal, Form, Input, Alert, Typography } from 'antd';

import { validateDeletionToken, DELETION_ITEM_TYPES } from '../../lib/deletion_token';

// Cómo se nombra cada cosa en el texto del modal.
const LABELS = {
  [DELETION_ITEM_TYPES.ACCOUNTING]: 'la contabilización',
  [DELETION_ITEM_TYPES.LINK]:       'el archivo vinculado',
  [DELETION_ITEM_TYPES.XML]:        'el archivo XML',
};

/**
 * @param {boolean}  open
 * @param {string}   itemType      uno de DELETION_ITEM_TYPES
 * @param {string}   itemName      nombre del archivo a borrar
 * @param {string}   customerId    extractCustomerId(ref.path)
 * @param {string}   customerName  razón social del cliente
 * @param {function} onCancel
 * @param {function} onConfirmed   se ejecuta SOLO si el token fue válido;
 *                                 aquí va el borrado real. Si lanza, el
 *                                 error se muestra y el modal sigue abierto.
 */
export function DeleteTokenModal({
  open,
  itemType,
  itemName,
  customerId,
  customerName,
  onCancel,
  onConfirmed,
}) {
  const firebase = useFirebase();
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Cada apertura empieza limpia: ni el token de la vez pasada ni su error.
  useEffect(() => {
    if (open) {
      setToken('');
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const handleOk = async () => {
    setBusy(true);
    setError(null);
    try {
      await validateDeletionToken(firebase, {
        token,
        itemType,
        itemName,
        customerId,
        customerName,
      });
      // Token válido → recién ahora se borra.
      await onConfirmed?.();
      onCancel?.();
    } catch (e) {
      setError(e?.message || 'No se pudo completar la eliminación.');
    } finally {
      setBusy(false);
    }
  };

  const label = LABELS[itemType] || 'el archivo';

  return (
    <Modal
      open={open}
      title="Confirmar eliminación"
      okText="Eliminar"
      cancelText="Cancelar"
      okButtonProps={{ danger: true, loading: busy, disabled: !token.trim() }}
      cancelButtonProps={{ disabled: busy }}
      onOk={handleOk}
      onCancel={busy ? undefined : onCancel}
      closable={!busy}
      maskClosable={false}
      destroyOnHidden
    >
      <p>
        Estás a punto de eliminar {label}
        {itemName ? <> <strong>{itemName}</strong></> : null}.
      </p>

      <Typography.Text type="secondary" style={{ fontSize: '0.9em' }}>
        Esta acción requiere el token de eliminación del mes. Si no lo tienes,
        solicítalo a tu supervisora.
      </Typography.Text>

      <Form layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item label="Ingresa tu Token de seguridad para confirmar:">
          <Input.Password
            placeholder="Token"
            value={token}
            autoFocus
            disabled={busy}
            onChange={(e) => setToken(e.target.value)}
            onPressEnter={() => { if (token.trim() && !busy) handleOk(); }}
          />
        </Form.Item>
      </Form>

      {error ? <Alert type="error" showIcon message={error} /> : null}
    </Modal>
  );
}
